import { useCallback, useMemo, useState } from 'react';

// The fields selection needs; both the Pending and Records rows have them.
export interface TableRow {
    id: string;
    deviceId: string;
    index: number;
}

const PAGE_LIMIT = 10;

/**
 * Paging and row selection over the already-filtered rows. Selection is stored as
 * row ids only; the selected rows, device ids and indexes are derived from `rows`,
 * so a row that disappears after a refetch (e.g. deleted) drops out of the
 * selection instead of lingering as a stale id.
 */
function useRecordTable<Row extends TableRow>(rows: Row[]) {
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState<string[]>([]);

    const pageCount = Math.ceil(rows.length / PAGE_LIMIT);
    const paginatedRecords = useMemo(() => rows.slice((page - 1) * PAGE_LIMIT, page * PAGE_LIMIT), [rows, page]);
    // Mobile infinite scroll shows everything up to the current page.
    const visibleRecords = useMemo(() => rows.slice(0, page * PAGE_LIMIT), [rows, page]);

    const selectedRows = useMemo(
        () => selected.map((id) => rows.find((row) => row.id === id)).filter((row): row is Row => !!row),
        [selected, rows]
    );
    const selectedDeviceIds = useMemo(() => selectedRows.map((row) => row.deviceId), [selectedRows]);
    const selectedIndexes = useMemo(() => selectedRows.map((row) => row.index), [selectedRows]);

    const isSelected = useCallback((id: string) => selected.includes(id), [selected]);

    const toggleRow = useCallback(
        (_event: unknown, id: string) =>
            setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])),
        []
    );

    // Header checkbox: selects the rows on the current page only.
    const toggleAll = useCallback(
        (event: any) => setSelected(event.target.checked ? paginatedRecords.map((row) => row.id) : []),
        [paginatedRecords]
    );

    const selectOnly = useCallback((row: Row) => setSelected([row.id]), []);

    // Pagination control: a new page starts with nothing selected.
    const changePage = useCallback((_event: unknown, newPage: number) => {
        setPage(newPage);
        setSelected([]);
    }, []);

    const loadMore = useCallback(() => setPage((prev) => (prev < pageCount ? prev + 1 : prev)), [pageCount]);

    return {
        page,
        setPage,
        pageCount,
        paginatedRecords,
        visibleRecords,
        selected,
        selectedRows,
        selectedDeviceIds,
        selectedIndexes,
        isSelected,
        toggleRow,
        toggleAll,
        selectOnly,
        changePage,
        loadMore,
    };
}

export default useRecordTable;
